import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { Card } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { Home, ChevronRight, Search, Plus, Edit2, Trash2, Filter, UserCircle } from 'lucide-react';

const AgentList = () => {
  const navigate = useNavigate();
  const [agents, setAgents] = useState([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState('');
  const [filterActive, setFilterActive] = useState('');
  const backendUrl = process.env.REACT_APP_BACKEND_URL;

  const getAuthHeaders = useCallback(() => {
    const token = localStorage.getItem('auth_token');
    return { 'Authorization': `Bearer ${token}`, 'Content-Type': 'application/json' };
  }, []);

  const fetchAgents = useCallback(async () => {
    try {
      setLoading(true);
      const params = new URLSearchParams();
      if (filterActive !== '') params.append('is_active', filterActive);
      const response = await fetch(`${backendUrl}/api/agents?${params.toString()}`, { headers: getAuthHeaders() });
      if (response.ok) {
        let data = await response.json();
        // Client-side search filter
        if (search) {
          const s = search.toLowerCase();
          data = data.filter(a => 
            a.agent_name?.toLowerCase().includes(s) ||
            a.country?.toLowerCase().includes(s) ||
            a.phone?.includes(s)
          );
        }
        setAgents(data);
      }
    } catch (error) {
      console.error('Error fetching agents:', error);
    } finally { setLoading(false); }
  }, [backendUrl, getAuthHeaders, search, filterActive]);

  useEffect(() => { fetchAgents(); }, [fetchAgents]);

  const handleDelete = async (id) => {
    if (!window.confirm('Deactivate this agent?')) return;
    try {
      const response = await fetch(`${backendUrl}/api/agents/${id}`, { method: 'DELETE', headers: getAuthHeaders() });
      if (response.ok) fetchAgents();
    } catch (error) { console.error('Error:', error); }
  };

  return (
    <div className="space-y-6" data-testid="agent-list-page">
      <div className="flex items-center gap-2 text-sm text-text-secondary">
        <Home className="w-4 h-4" />
        <span className="cursor-pointer hover:text-accent-blue" onClick={() => navigate('/')}>Home</span>
        <ChevronRight className="w-4 h-4" />
        <span className="cursor-pointer hover:text-accent-blue" onClick={() => navigate('/settings')}>Settings</span>
        <ChevronRight className="w-4 h-4" />
        <span className="text-text-dark font-medium">Agent Master</span>
      </div>

      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-bold text-text-dark font-manrope">Agent Master</h1>
          <p className="text-sm text-text-secondary font-ibm mt-1">{agents.length} agents</p>
        </div>
        <Button onClick={() => navigate('/settings/agents/add')} data-testid="add-agent-btn" className="bg-accent-blue hover:bg-accent-blue/90">
          <Plus className="w-4 h-4 mr-2" /> Add Agent
        </Button>
      </div>

      <Card className="bg-white rounded-xl border border-gray-100 shadow-sm p-4">
        <div className="flex items-center gap-2 mb-3">
          <Filter className="w-4 h-4 text-text-secondary" />
          <span className="text-sm font-medium text-text-dark font-ibm">Filters</span>
        </div>
        <div className="grid grid-cols-2 md:grid-cols-3 gap-3">
          <div className="relative">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400" />
            <Input data-testid="agent-search" placeholder="Search name, country, phone..." value={search} onChange={e => setSearch(e.target.value)} className="pl-10 h-9 text-sm border-gray-200" />
          </div>
          <Select value={filterActive === '' ? "all" : filterActive} onValueChange={v => setFilterActive(v === "all" ? '' : v)}>
            <SelectTrigger data-testid="filter-agent-active" className="h-9 text-sm"><SelectValue placeholder="All Status" /></SelectTrigger>
            <SelectContent>
              <SelectItem value="all">All Status</SelectItem>
              <SelectItem value="true">Active</SelectItem>
              <SelectItem value="false">Inactive</SelectItem>
            </SelectContent>
          </Select>
        </div>
      </Card>

      <Card className="bg-white rounded-xl border border-gray-100 shadow-sm overflow-hidden">
        <div className="overflow-x-auto">
          <table className="w-full" data-testid="agent-table">
            <thead>
              <tr className="bg-gray-50 border-b border-gray-200">
                {['Agent Name', 'Phone', 'Address', 'Country', 'Status', 'Actions'].map(h => (
                  <th key={h} className="px-4 py-3 text-left text-xs font-semibold text-text-secondary font-ibm uppercase tracking-wider">{h}</th>
                ))}
              </tr>
            </thead>
            <tbody className="divide-y divide-gray-100">
              {loading ? (
                <tr><td colSpan={6} className="px-4 py-8 text-center text-text-secondary font-ibm">Loading...</td></tr>
              ) : agents.length === 0 ? (
                <tr><td colSpan={6} className="px-4 py-8 text-center text-text-secondary font-ibm">No agents found</td></tr>
              ) : (
                agents.map(a => (
                  <tr key={a.id} className="hover:bg-gray-50/50 transition-colors" data-testid={`agent-row-${a.id}`}>
                    <td className="px-4 py-3">
                      <div className="flex items-center gap-2">
                        <UserCircle className="w-4 h-4 text-teal-500 shrink-0" />
                        <span className="font-medium text-sm text-text-dark font-ibm">{a.agent_name}</span>
                      </div>
                    </td>
                    <td className="px-4 py-3 text-sm text-text-dark font-ibm">{a.phone || '—'}</td>
                    <td className="px-4 py-3 text-sm text-text-dark font-ibm max-w-xs truncate">{a.address || '—'}</td>
                    <td className="px-4 py-3 text-sm text-text-dark font-ibm">{a.country || '—'}</td>
                    <td className="px-4 py-3">
                      <span className={`inline-flex items-center px-2 py-0.5 rounded text-xs font-medium ${a.is_active ? 'bg-green-100 text-green-700' : 'bg-red-100 text-red-700'}`}>
                        {a.is_active ? 'Active' : 'Inactive'}
                      </span>
                    </td>
                    <td className="px-4 py-3">
                      <div className="flex gap-1">
                        <Button size="icon" variant="ghost" className="h-7 w-7" data-testid={`edit-agent-${a.id}`} onClick={() => navigate(`/settings/agents/edit/${a.id}`)}>
                          <Edit2 className="w-3.5 h-3.5 text-text-secondary" />
                        </Button>
                        <Button size="icon" variant="ghost" className="h-7 w-7" data-testid={`delete-agent-${a.id}`} onClick={() => handleDelete(a.id)}>
                          <Trash2 className="w-3.5 h-3.5 text-red-400" />
                        </Button>
                      </div>
                    </td>
                  </tr>
                ))
              )}
            </tbody>
          </table>
        </div>
      </Card>
    </div>
  );
};

export default AgentList;
